// MyOverview — donor-side Overview at /me. γ.1 of the donor-portal
// program (CHG-1501 / ITER-3618), retargeted by CHG-2946 (Slice E,
// program §8 decision 5) to the enriched GET /api/v1/my-giving/summary
// (PortalGivingSummary) — one round-trip landing payload composed
// BE-side from the existing per-domain services.
//
// Slots in DashboardPage:
//   - StatStrip (4 cards): Lifetime given, This year, Active recurring,
//     Open pledges
//   - feed slot: donor-flavored recent-donations list (last 5 from
//     summary.recent_donations, refund-aware per CHG-2934) — inline,
//     not a generic primitive yet (the donor-flavored DonationsFeed is
//     a δ-phase scope item per the program plan)
//   - callout slot: next-charge teaser (summary.next_recurring_charge)
//     + open-pledge per-currency balances; null when neither exists
//   - Per-currency rollup: rendered below the StatStrip as a small
//     "Across N currencies" chip row when the donor has mixed-currency
//     totals (PortalGivingSummary.currency=null). When single-currency
//     (currency !== null), the rollup row is hidden — the StatStrip
//     already shows the canonical totals in that currency.
//
// Donor scoping: the endpoint resolves donor_ids from the authenticated
// account server-side. Page passes no donor_id. Auth gated by
// <ProtectedRoute> (no requirePermission — donor auth model, mirrors
// β.1/β.2/β.5 precedent).

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardPage } from "../components/composition/DashboardPage";
import { StatStrip, type StatStripItem } from "../components/composition/StatStrip";
import {
  meOverviewApi,
  type PortalGivingSummary,
  type PortalNextRecurringCharge,
  type PortalDonationRead,
  type PortalCurrencyTotal,
} from "../api/me-overview";

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function statsToItems(data: PortalGivingSummary | undefined): StatStripItem[] {
  // Single-currency: StatStrip shows real currency on the cents cards;
  // mixed-currency: cents fields are 0 (per BE contract), but the page
  // surfaces the per-currency rollup row below — so render the StatStrip
  // values as 0 with a placeholder currency rather than misleading "$0".
  const currency = data?.currency ?? "USD";
  return [
    {
      id: "lifetime",
      label: "Lifetime given",
      value: data?.lifetime_total_minor_units ?? null,
      format: "currency",
      currency,
      tone: "g-giving",
    },
    {
      id: "ytd",
      label: "This year",
      value: data?.annual_total_minor_units ?? null,
      format: "currency",
      currency,
      tone: "g-giving",
    },
    {
      id: "active_recurring",
      label: "Active recurring",
      value: data?.active_recurring_count ?? null,
      format: "number",
      tone: "g-automation",
    },
    {
      id: "open_pledges",
      label: "Open pledges",
      value: data?.open_pledges_count ?? null,
      format: "number",
      tone: "g-events",
    },
  ];
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = d.getTime() - Date.now();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (Math.abs(diffDays) >= 1) return RELATIVE.format(diffDays, "day");
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  return RELATIVE.format(diffHours, "hour");
}

function formatAmount(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/** CHG-2934 (review issue 5) — recent-donations amount cell. Same
 *  effective-amount + refunded-caption treatment as MyGiving's
 *  DonationAmountCell so the landing feed reconciles with the list and
 *  the net /summary totals (PortalDonationRead already carries
 *  refunded_amount_minor_units / effective_amount_minor_units). */
function RecentDonationAmount({ donation }: { donation: PortalDonationRead }) {
  const refunded = donation.refunded_amount_minor_units ?? 0;
  if (refunded <= 0) {
    return <>{formatAmount(donation.amount_minor_units, donation.currency)}</>;
  }
  const effective =
    donation.effective_amount_minor_units ??
    Math.max(0, donation.amount_minor_units - refunded);
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
      <span>{formatAmount(effective, donation.currency)}</span>
      <span style={{ fontSize: 11, fontWeight: 400, color: "var(--color-text-muted)" }}>
        {formatAmount(refunded, donation.currency)} refunded
      </span>
    </span>
  );
}

/** Donor-flavored recent-donations list. Inline per the program plan
 *  doctrine (donor-flavored DonationsFeed primitive is a δ-phase scope
 *  item; γ.1 ships its own minimal feed to avoid blocking on a primitive
 *  extraction CHG). Exported for the page test suite. CHG-2946 (Slice E)
 *  wired the page to feed it summary.recent_donations — the card renders
 *  real landing data (refund-aware via RecentDonationAmount). */
export function RecentDonationsCard({ donations }: { donations: PortalDonationRead[] }) {
  if (donations.length === 0) {
    return (
      <section
        className="card"
        aria-labelledby="my-recent-donations-title"
        style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}
      >
        <h2 id="my-recent-donations-title" style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
          Recent donations
        </h2>
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>
          You haven&rsquo;t made any donations yet. Once you do, your most recent gifts will appear here.
        </p>
      </section>
    );
  }
  return (
    <section
      className="card"
      aria-labelledby="my-recent-donations-title"
      style={{ padding: 0, display: "flex", flexDirection: "column" }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 16px",
          borderBottom: "1px solid var(--color-border-subtle)",
        }}
      >
        <h2 id="my-recent-donations-title" style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
          Recent donations
        </h2>
      </header>
      <ul role="list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {donations.map((d) => (
          <li
            key={d.id}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 8,
              padding: "12px 16px",
              borderBottom: "1px solid var(--color-border-subtle)",
              alignItems: "center",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text)" }}>
                {d.org_name ?? "Organization"}
              </div>
              <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                {d.designation_name ?? "General"}{d.session_name ? ` · ${d.session_name}` : ""}
                {" · "}
                {formatRelative(d.paid_at ?? d.created_at)}
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)" }}>
              <RecentDonationAmount donation={d} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** CHG-2946 (Slice E) — right-rail landing callout: next-recurring-charge
 *  teaser + open-pledge per-currency balances. The page passes callout=null
 *  (single-column grid) when the donor has neither an upcoming charge nor
 *  an open pledge; tests exercise both paths through the page. Exported
 *  for component-level testing (same pattern as RecentDonationsCard). */
export function NextStepsCallout({
  nextCharge,
  pledgeCommitted,
  pledgeRemaining,
}: {
  nextCharge: PortalNextRecurringCharge | null;
  pledgeCommitted: PortalCurrencyTotal[];
  pledgeRemaining: PortalCurrencyTotal[];
}) {
  const committedByCurrency = new Map(pledgeCommitted.map((t) => [t.currency, t]));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {nextCharge ? (
        <section
          className="card"
          aria-labelledby="my-next-charge-title"
          style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}
        >
          <h2 id="my-next-charge-title" style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
            Next gift
          </h2>
          <div style={{ fontSize: 13, color: "var(--color-text)" }}>
            <span style={{ fontWeight: 600 }}>
              {formatAmount(nextCharge.amount_minor_units, nextCharge.currency)}
            </span>
            {" to "}
            {nextCharge.org_name ?? "Organization"}
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
            Scheduled {formatRelative(nextCharge.next_charge_at)}
          </div>
        </section>
      ) : null}
      {pledgeRemaining.length > 0 ? (
        <section
          className="card"
          aria-labelledby="my-pledge-balance-title"
          style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}
        >
          <h2 id="my-pledge-balance-title" style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
            Open pledge balance
          </h2>
          <ul role="list" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {pledgeRemaining.map((t) => {
              const committed = committedByCurrency.get(t.currency);
              return (
                <li key={t.currency} style={{ fontSize: 13, color: "var(--color-text)" }}>
                  <span style={{ fontWeight: 600 }}>{formatAmount(t.total_minor_units, t.currency)}</span>
                  <span style={{ color: "var(--color-text-muted)" }}>
                    {" remaining"}
                    {committed ? ` of ${formatAmount(committed.total_minor_units, t.currency)} committed` : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** Per-currency rollup chip row — only shown when donor's totals span
 *  multiple currencies (PortalGivingSummary.currency === null). Helps the
 *  donor see "BDT 200,000 + $500" rather than the misleading $0.00 the
 *  zeroed scalar produces. */
function PerCurrencyRollup({ totals, label }: { totals: PortalCurrencyTotal[]; label: string }) {
  if (totals.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
      <span style={{ fontSize: 11, color: "var(--color-text-muted)", marginRight: 4 }}>{label}:</span>
      {totals.map((t) => (
        <span
          key={t.currency}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            background: "var(--color-bg-subtle)",
            borderRadius: 9999,
            fontSize: 11,
            color: "var(--color-text)",
          }}
        >
          {formatAmount(t.total_minor_units, t.currency)}
          <span style={{ color: "var(--color-text-muted)" }}>· {t.count}</span>
        </span>
      ))}
    </div>
  );
}

export function MyOverview() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["me-overview"],
    queryFn: meOverviewApi.fetchMyOverview,
  });

  const items = useMemo(() => statsToItems(data), [data]);
  const mixedCurrency = data?.currency === null && (data?.lifetime_per_currency.length ?? 0) > 0;

  // CHG-2946 (Slice E) — landing wiring: recent gifts come INLINE from
  // the enriched /my-giving/summary payload (pre-fix the page hardcoded
  // donations={[]}, so the recent-gifts card was always empty). The
  // callout slot stays null (single-column grid) unless the donor has an
  // upcoming charge or an open pledge balance to act on.
  const recentDonations = data?.recent_donations ?? [];
  const nextCharge = data?.next_recurring_charge ?? null;
  const pledgeCommitted = data?.pledge_committed_per_currency ?? [];
  const pledgeRemaining = data?.pledge_remaining_per_currency ?? [];
  const hasCallout = nextCharge !== null || pledgeRemaining.length > 0;

  return (
    <DashboardPage
      title="My giving"
      subtitle="Your impact across every organization you support."
      stats={
        <>
          <StatStrip items={items} loading={isLoading} />
          {mixedCurrency ? (
            <div style={{ marginTop: 4 }}>
              <PerCurrencyRollup totals={data!.lifetime_per_currency} label="Lifetime per currency" />
              <PerCurrencyRollup totals={data!.annual_per_currency} label="This year per currency" />
            </div>
          ) : null}
        </>
      }
      feed={<RecentDonationsCard donations={recentDonations} />}
      callout={
        hasCallout ? (
          <NextStepsCallout
            nextCharge={nextCharge}
            pledgeCommitted={pledgeCommitted}
            pledgeRemaining={pledgeRemaining}
          />
        ) : null
      }
      loading={isLoading}
      error={error as Error | null}
      onRetry={() => refetch()}
    />
  );
}
